import React, { useRef, useEffect, useState, useCallback } from 'react'
import { Box } from '@chakra-ui/react'
import LumeneLogo from './LumeneLogo'
import Viewfinder from './Viewfinder'
import ContextBadge from './ContextBadge'
import CaptureButton from './CaptureButton'
import CameraPermissionError from './CameraPermissionError'

const CameraView = ({ onCapture }) => {
  const videoRef  = useRef(null)
  const streamRef = useRef(null)
  const [permissionDenied, setPermissionDenied] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setPermissionDenied(true)
      return
    }
    let cancelled = false
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'environment' } })
      .then((stream) => {
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return }
        streamRef.current = stream
        if (videoRef.current) videoRef.current.srcObject = stream
      })
      .catch(() => setPermissionDenied(true))

    return () => {
      cancelled = true
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop())
        streamRef.current = null
      }
    }
  }, [])

  const capture = useCallback(async () => {
    if (!videoRef.current || isLoading) return
    const canvas = document.createElement('canvas')
    canvas.width  = videoRef.current.videoWidth
    canvas.height = videoRef.current.videoHeight
    canvas.getContext('2d').drawImage(videoRef.current, 0, 0, canvas.width, canvas.height)

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92))
    if (!blob) return

    setIsLoading(true)
    try {
      await onCapture(blob)
    } finally {
      setIsLoading(false)
    }
  }, [isLoading, onCapture])

  if (permissionDenied) return <CameraPermissionError />

  return (
    <Box position="relative" w="100%" minH="100dvh" overflow="hidden" bg="lumene.night">
      <video
        ref={videoRef}
        autoPlay playsInline muted
        style={{ width: '100%', height: '100dvh', objectFit: 'cover', display: 'block' }}
      />

      <Box
        position="absolute"
        top="calc(env(safe-area-inset-top, 0px) + 16px)"
        left="50%"
        transform="translateX(-50%)"
        zIndex={2}
      >
        <LumeneLogo size="sm" isScanning={isLoading} />
      </Box>

      <Viewfinder isScanning={isLoading} />

      <Box
        position="absolute"
        left="50%"
        bottom="130px"
        transform="translateX(-50%)"
        zIndex={2}
      >
        <ContextBadge text={isLoading ? 'Identification en cours…' : 'Pointez une œuvre'} />
      </Box>

      <Box
        position="absolute"
        left="50%"
        bottom="calc(env(safe-area-inset-bottom, 0px) + 38px)"
        transform="translateX(-50%)"
        zIndex={2}
      >
        <CaptureButton onCapture={capture} isLoading={isLoading} />
      </Box>
    </Box>
  )
}

export default CameraView
